import { type Dispatch, type SetStateAction } from "react";

import { Alert, AlertTitle, Button, capitalize, Stack, Typography } from "@mui/material";

import { useApiContext } from "../../../../../api/context";
import { useCustomerId } from "../../../../../Components/hooks/useCustomerId";
import { useErrorSnackbar, useSuccessSnackbar } from "../../../../../Components/SharedSnackbar/SharedSnackbar.context";
import useSegmentTrackEvent from "../../../../../utils/useSegmentTrackEvent";
import { CONNECTION_TEST_EVENT, createConnectionTestEventProperties, OPENAI_FEATURE } from "../../segmenTracking";
import { type FormState } from "../types";

type Props = {
  formState: FormState;
  onChange: Dispatch<SetStateAction<FormState>>;
};

interface APIError {
  response?: {
    data?: {
      error?: string;
    };
    status?: number;
  };
}

export default function TestConnectionStep({ formState, onChange }: Props) {
  const customerId = useCustomerId();
  const api = useApiContext();
  const showErrorSnackbar = useErrorSnackbar();
  const showSuccessSnackbar = useSuccessSnackbar(1);
  const { trackEvent } = useSegmentTrackEvent();

  const testConnection = async () => {
    let connectionTestResult = false;
    onChange((state) => ({
      ...state,
      loading: true,
      successMessage: "You have successfully connected your OpenAI account and we will start importing your data.",
    }));

    const trackConnectionTest = (result: "success" | "failed", extraProps: Record<string, unknown> = {}) => {
      trackEvent(CONNECTION_TEST_EVENT, {
        ...createConnectionTestEventProperties(OPENAI_FEATURE),
        source: "TestConnectionStep",
        connectionTestResult: result,
        ...extraProps,
      });
    };

    const { adminKey } = formState;
    let connectionError = "";

    try {
      await api.post(`/v1/customers/${customerId}/integrations/openai/action/onboard`, {
        adminKey,
      });

      connectionTestResult = true;
      showSuccessSnackbar("Success");
      trackConnectionTest("success");
    } catch (error) {
      const apiError = error as APIError;
      const errorMessage = capitalize(apiError.response?.data?.error || "");
      const statusCode = apiError.response?.status;

      if (statusCode === 409) {
        showSuccessSnackbar("Success");
        connectionTestResult = true;
        trackConnectionTest("success");

        onChange((state) => ({
          ...state,
          successMessage: "This organization has been previously onboarded.",
        }));
      } else {
        const defaultErrorMessage = "Error testing connection. Check the credentials and try again.";
        connectionError = errorMessage || defaultErrorMessage;
        showErrorSnackbar(connectionError);
        trackConnectionTest("failed", { error: errorMessage });
      }
    }

    onChange((state) => ({ ...state, connectionTestResult, connectionError, loading: false }));
  };

  return (
    <Stack data-cy="test-connection-step" spacing={4}>
      <Typography variant="h3">Test your OpenAI connection with DoiT</Typography>

      <Stack spacing={2}>
        <Typography variant="body1">
          To make sure the setup is successful, we must test the connection. This should take a couple of seconds.{" "}
        </Typography>
        <Button
          variant="outlined"
          sx={{ width: "140px" }}
          onClick={testConnection}
          disabled={formState.connectionTestResult !== null}
          loading={formState.loading}
        >
          Test connection
        </Button>
        {formState.connectionTestResult === false && (
          <Alert severity="error">
            <Stack>
              <AlertTitle>Test failed</AlertTitle>
              {formState.connectionError}
            </Stack>
          </Alert>
        )}
        {formState.connectionTestResult && (
          <Alert severity="success">
            <AlertTitle>Test successful</AlertTitle>
            <Typography variant="body1">{formState.successMessage}</Typography>
          </Alert>
        )}
      </Stack>
    </Stack>
  );
}
